import React, { useState, useEffect, useId, useRef } from "react";
import * as c from "./wistiaVideoPopover.module.scss";
import classNames from "classnames";
import useWistiaPlayer from "@hooks/useWistiaPlayer";
import WistiaScripts from "./wistiaScripts";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";

const WistiaVideoPopover = ({ className, wistiaVideoId, trackedEventName = null, children }) => {
  const [isSSR, setIsSSR] = useState(true);
  const [isInView, setIsInView] = useState(false);
  const [isWistiaEmbedded, setIsWistiaEmbedded] = useState(false);

  const wistiaContainerId = useId();
  const containerRef = useRef(null);

  const { wistiaPlayer } = useWistiaPlayer({
    wistiaContainerId,
    trackedEventName,
  });

  // Since wistia injects into the DOM when it has loaded clientside, we have
  // to make sure it isn't rendered on the server or else we'll get a mismatch
  // between the server and client during hydration
  useEffect(() => {
    setIsSSR(false);
  }, []);

  useGSAP(() => {
    if (!isSSR) {
      gsap.registerPlugin(ScrollTrigger);
      ScrollTrigger.create({
        trigger: containerRef.current,
        start: () => "top 100%+=20px",
        invalidateOnRefresh: true,
        once: true,
        onEnter: () => {
          setIsInView(true);
        },
      });
    }
  }, [isInView, isSSR]);

  useEffect(() => {
    if (wistiaPlayer && wistiaPlayer.ready()) {
      setIsWistiaEmbedded(true);
    }
  }, [wistiaPlayer]);

  const togglePopover = () => {
    if (isWistiaEmbedded) {
      wistiaPlayer.popover.show();
      wistiaPlayer.play();
    }
  };

  return (
    <>
      <div className={className} onClick={togglePopover} ref={containerRef}>
        {!isSSR && isInView && (
          <>
            <WistiaScripts wistiaVideoId={wistiaVideoId} />
            <div
              id={wistiaContainerId} // Set an id so we have a unique identifier to hook into using the wistia api
              className={classNames(
                "wistia_embed",
                `wistia_async_${wistiaVideoId}`,
                "seo=false",
                "videoFoam=true",
                "popover=true",
                "popoverContent=link"
              )}
            ></div>
            <div className={classNames({ [c.hidden]: !isWistiaEmbedded })}>{children}</div>
          </>
        )}
        {/*
          Render a duplicate of the children on the server and then hide the element when
          wistia is done manipulating the DOM. This is necessary to mask the layout shift
          that would otherwise happen if we waited to render until the component hydrates.
        */}
        <div className={classNames({ [c.hidden]: isWistiaEmbedded })}>{children}</div>
      </div>
    </>
  );
};

export default WistiaVideoPopover;
